<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else-if="isError" class="no-print">
    <div class="text-center mt-5">
      <h1 class="text-2xl">Error loading work order</h1>
      <p class="text-lg">Please try again later</p>
    </div>
  </div>

  <div v-else>
    <!-- menu -->
    <div class="flex justify-between items-center bg-white p-4 shadow-md fixed top-0 left-0 right-0 z-10">
      <!-- logo -->
      <div>
        <img :src="logo" alt="uttrly logo" class="h-10" />
      </div>
    </div>

    <div class="w-full">
      <div class="mt-20 max-w-screen-md mx-4 md:mx-8 lg:mx-auto">
        <div class="pt-10 mb-4 md:mb-12 text-white">
          <h2 class="">Work Order for {{ sub.name }}</h2>
          <span v-if="sub.address" class="mt-2 text-base inline-block">{{ sub.address }}</span>
        </div>

        <!-- work order details -->
        <div class="paper md:mt-4 px-6 py-8 rounded-lg text-sm ">
          <!-- header informations -->
          <div class="grid grid-cols-2 mb-8">
            <!-- uttrly informations -->
            <div>
              <div class="mb-4">
                <span class="block font-bold text-md mb-3">Uttrly</span>
                <span class="block mb-2">11810 Grand Park Ave, Suite 500</span>
                <span class="block mb-2">North Bethesda, MD 20852</span>
                <span class="block mb-2">202.681.0825</span>
                <span class="block">portal-admin@uttrly.com</span>
              </div>

              <div class="text-primary-700 font-semibold mb-4 text-2xl">
                <span>Work Order</span>
              </div>

              <!-- sub informations -->
              <div>
                <span v-if="sub?.name" class="block mb-1 font-bold">{{ sub.name }}</span>
                <span v-if="sub?.address" class="block mb-1">{{ sub.address }}</span>
              </div>
            </div>

            <!-- log and work order header informations -->
            <div>
              <div class="flex justify-end mb-7">
                <img
                  :src="logo"
                  alt="Uttrly"
                />
              </div>
              <div class="text-right">
                <div class="grid grid-cols-4 gap-2">
                  <span class="font-bold col-span-3">WO#</span>
                  <span>{{ workOrder.workOrderId }}</span>
                </div>

                <div class="grid grid-cols-4 gap-2">
                  <span class="font-bold col-span-3">DATE</span>
                  <span>{{ getLocalDate(workOrder.date) }}</span>
                </div>

                <div class="grid grid-cols-4 gap-2">
                  <span class="font-bold col-span-3">TERMS</span>
                  <span>Net 30</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Project site -->
          <div class="mb-4">
            <span class="font-bold text-md mb-3">Project Site: </span>
            <span>{{ project?.address }}</span>
          </div>

          <!-- Work order items -->
          <div class="pt-3 mt-3">
            <div>
              <h3 class="text-xl font-medium leading-10 tracking-tight">Details</h3>
            </div>

            <!-- work order items -->
            <div class="mt-2">
              <div
                v-for="(woItem, idx) in workOrder.items"
                :key="woItem.id"
                :class="[
                  idx != workOrder.items.length - 1 ? 'border-b border-gray-200 py-4' : 'py-4'
                ]"
              >
                <div>
                  <div class="grid grid-cols-5 gap-4">
                    <div class="col-span-4 flex items-start">
                      <BaseCollapse
                        :bg-color="'bg-withe'"
                        :default-open="idx === 0"
                        class="!pl-0 pt-0"
                      >
                        <template #header>
                          <div class="flex items-center">
                            <span class="font-bold">{{ woItem.name }}</span>
                          </div>
                        </template>

                        <div class="text-justify" v-html="woItem.scopeOfWork" />
                      </BaseCollapse>
                    </div>


                    <!-- line item total -->
                    <div class="text-right text-sm pt-2">
                      <span class="font-semibold">{{ currency(woItem.cost) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- total -->
            <hr class="border-b-slate-200 border-2 mt-3" />

            <div class="mt-4 mb-8">
              <div class="grid grid-cols-5 gap-4">
                <div class="col-span-4">
                  <span class="text-slate-500 text-xs">Thank you for your business and have a great day</span>
                </div>

                <div>
                  <div class="flex items-center justify-end gap-2">
                    <span class="font-bold">Total:</span>
                    <span>{{ currency(workOrder.total) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { Project } from '@/models/project'
import { Subcontractor } from '@/models/subcontractor'
import { WorkOrder } from '@/models/workOrder'
import FirebaseService from '@/services/FirebaseService'
import { useNotification } from '@kyvg/vue3-notification'
import { useQuery } from '@tanstack/vue-query'
import { useHead } from '@unhead/vue'
import { httpsCallable } from 'firebase/functions'
import { extend } from 'underscore'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import logo from '@/assets/images/logo.png'
import currency from '@/utils/currency'
import { getLocalDate } from '@/utils'
import BaseCollapse from '@/components/base/BaseCollapse.vue'


useHead({ title: 'Work Order' })

const { notify } = useNotification()
const $route = useRoute()
const id = $route.params.id as string

const project = ref(new Project())
const sub = ref(new Subcontractor())
const workOrder = ref(new WorkOrder())

const { isLoading, isError } = useQuery({
  queryKey: ['workOrder', id],
  queryFn: async () => {
    const functions = await FirebaseService.functions()
    const fetchData = httpsCallable(functions, 'workOrder-fetchWorkOrderDataV2')
    const response = await fetchData({ id })

    try {
      const data = response.data as any
      project.value = extend(project.value, data.project) as Project
      sub.value = extend(sub.value, data.sub) as Subcontractor
      workOrder.value = extend(workOrder.value, data.workOrder) as WorkOrder


      return { project, sub, workOrder}
    } catch (err) {
      console.log(err)
      notify({
        type: 'error',
        title: 'Error',
        text: 'Error trying to fetch data'
      })
    }
  }
})


</script>
